import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle, ArrowUpRight } from "lucide-react";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", data: null, error: null });
  const attemptsRef = useRef(0);
  const stoppedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", error: "Missing session id", data: null });
      return;
    }

    const poll = async () => {
      if (stoppedRef.current) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", data, error: null });
          stoppedRef.current = true;
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState({ status: "failed", data, error: null });
          stoppedRef.current = true;
          return;
        }
        if (attemptsRef.current >= 8) {
          setState({ status: "timeout", data, error: null });
          stoppedRef.current = true;
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setState({ status: "error", error: e.response?.data?.detail || e.message, data: null });
        stoppedRef.current = true;
      }
    };
    poll();

    return () => {
      stoppedRef.current = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-20">
      <div className="max-w-lg w-full text-center" data-testid="billing-success-state">
        {state.status === "polling" && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 border border-white/20 flex items-center justify-center">
              <Loader2 className="text-[var(--primary)] animate-spin" size={28} strokeWidth={1.5} />
            </div>
            <h2 className="font-heading font-black text-4xl md:text-5xl tracking-tighter mb-4">
              Confirming your payment…
            </h2>
            <p className="text-zinc-400">Sit tight, this takes a second.</p>
          </>
        )}

        {state.status === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 border border-[var(--primary)]/60 bg-[var(--primary)]/10 flex items-center justify-center">
              <CheckCircle2 className="text-[var(--primary)]" size={32} strokeWidth={1.5} />
            </div>
            <div className="tag mb-4 mx-auto">// payment received</div>
            <h2 className="font-heading font-black text-4xl md:text-5xl tracking-tighter mb-4">
              You&apos;re in.
            </h2>
            <p className="text-zinc-300 mb-2">
              {state.data?.plan_name ? `${state.data.plan_name} plan activated.` : "Plan activated."}
            </p>
            <p className="text-zinc-500 text-sm font-mono">
              Reference: {sessionId?.slice(0, 24)}…
            </p>
            <div className="mt-10 flex flex-wrap gap-3 items-center justify-center">
              <Link to="/onboarding" className="btn-primary" data-testid="billing-goto-onboarding">
                Start Structure Onboarding <ArrowUpRight size={16} />
              </Link>
              <Link to="/dashboard" className="btn-ghost">
                Skip for now
              </Link>
            </div>
            <p className="mt-6 text-xs text-zinc-500 max-w-sm mx-auto">
              Complete a single intake and we&rsquo;ll auto-file your registrations across every royalty source you select.
            </p>
          </>
        )}

        {(state.status === "failed" || state.status === "error" || state.status === "timeout") && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 border border-red-500/60 bg-red-500/10 flex items-center justify-center">
              <XCircle className="text-red-400" size={28} strokeWidth={1.5} />
            </div>
            <h2 className="font-heading font-black text-4xl md:text-5xl tracking-tighter mb-4">
              {state.status === "timeout"
                ? "Still confirming…"
                : "Something went wrong."}
            </h2>
            <p className="text-zinc-400 mb-8">
              {state.error ||
                "If your card was charged we'll detect it shortly. Check your email or reach out to support."}
            </p>
            <div className="flex items-center gap-3 justify-center">
              <Link to="/pricing" className="btn-ghost">Back to pricing</Link>
              <Link to="/contact" className="btn-primary">Contact support <ArrowUpRight size={14} /></Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
